import { keepaliveRequest } from '@/modules/common/utils/apiRequestUtils'

// Only primitives: an object is serialized whole, and a request error carries its body and auth header.
type RemoteLogValue = string | number | boolean | null | undefined

type RemoteLogContext = Record<string, RemoteLogValue>

type RemoteLogLevel = 'info' | 'warning' | 'error'

export interface RemoteLogArgs {
	message: string
	context?: RemoteLogContext
}

interface RemoteLogEntry {
	level: RemoteLogLevel
	message: string
	context: RemoteLogContext
	usage: boolean
}

// The endpoint refuses a larger batch, or a longer value — and with it the whole batch.
const MAX_BATCH_SIZE = 20

const MAX_VALUE_LENGTH = 1000

const FLUSH_DELAY_MS = 2000

const primitiveOrUndefined = (value: unknown): RemoteLogValue =>
	['string', 'number', 'boolean'].includes(typeof value) ? (value as RemoteLogValue) : undefined

const truncated = (context: RemoteLogContext): RemoteLogContext =>
	Object.fromEntries(
		Object.entries(context).map(([key, value]) => [
			key,
			typeof value === 'string' ? value.slice(0, MAX_VALUE_LENGTH) : value,
		]),
	)

export function errorSummary(error: unknown): RemoteLogContext {
	const details = (typeof error === 'object' && error !== null ? error : {}) as Record<string, any>

	return {
		name: primitiveOrUndefined(details.name),
		message: primitiveOrUndefined(details.message ?? details.description),
		code: primitiveOrUndefined(details.code ?? details.error),
		type: primitiveOrUndefined(details.type),
		status: primitiveOrUndefined(details.statusCode ?? details.status ?? details.response?.status),
	}
}

export class RemoteLog {
	private static pending: RemoteLogEntry[] = []

	private static flushTimer: number | undefined

	private static page: string | undefined

	private static logToRemote(
		level: RemoteLogLevel,
		message: string,
		context: RemoteLogContext,
		usage = false,
	) {
		this.pending.push({
			level,
			message,
			context: truncated({ page: this.page, ...context }),
			usage,
		})

		if (this.pending.length >= MAX_BATCH_SIZE) {
			this.flush()
			return
		}

		this.flushTimer ??= window.setTimeout(() => this.flush(), FLUSH_DELAY_MS)
	}

	// Kept clear of axios's 401 handler, which would log the client out over a failed log request.
	public static flush() {
		window.clearTimeout(this.flushTimer)
		this.flushTimer = undefined

		if (this.pending.length === 0) {
			return
		}

		const entries = this.pending
		this.pending = []

		keepaliveRequest('client-dashboard-logs', { entries }).catch(() => {
			console.error('Logging to Remote Logging Failed')
		})
	}

	public static setPage(page: string | undefined) {
		this.page = page
	}

	// The AD writes these to their own stream, as it does its own request lines.
	public static usage(args: RemoteLogArgs) {
		this.logToRemote('info', args.message, args.context ?? {}, true)
	}

	public static info(args: RemoteLogArgs) {
		this.logToRemote('info', args.message, args.context ?? {})
	}

	public static warning(args: RemoteLogArgs) {
		this.logToRemote('warning', args.message, args.context ?? {})
	}

	public static error(args: RemoteLogArgs) {
		this.logToRemote('error', args.message, args.context ?? {})
	}
}

document.addEventListener('visibilitychange', () => {
	if (document.visibilityState === 'hidden') {
		RemoteLog.flush()
	}
})
